import React from "react";
import HeaderBar from "../widgets2024/HeaderBar";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EventIcon from "@mui/icons-material/Event";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

const navItems = {
  rootPath: "/home",
  barLogo:
    "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRme8ngSadIpD8MTZ_EoRLsvdYE6CRJRtNVz1JgZkvEWqW1j1dmKHf-fEAMa5LtQSt2sXo&usqp=CAU",
  barName: "ExpoPlanner",
  profileName: "Super Admin",
  leftItems: [
    {
      label: "Add Organiser",
      path: "/add-organiser",
      icon: "AddIcon",
    },
  ],
  rightItems: [
    {
      label: "Profile",
      icon: "AccountCircle",
      path: "/profile",
    },
    {
      label: "Logout",
      icon: "LogoutIcon",
      path: "/logout",
    },
  ],
};

const statCards = [
  {
    label: "Total Organisers",
    value: "12",
    icon: <PeopleAltIcon sx={{ fontSize: 40 }} />,
    color: "#1976d2",
    bg: "#e3f2fd",
  },
  {
    label: "Total Events",
    value: "34",
    icon: <EventIcon sx={{ fontSize: 40 }} />,
    color: "#388e3c",
    bg: "#e8f5e9",
  },
  {
    label: "Total Visitors",
    value: "5,210",
    icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
    color: "#f57c00",
    bg: "#fff3e0",
  },
  {
    label: "Total Exhibitors",
    value: "318",
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    color: "#7b1fa2",
    bg: "#f3e5f5",
  },
];

const recentEvents = [
  {
    id: 1,
    name: "IMTEX 2025",
    organiser: "IMTMA",
    city: "Bengaluru",
    country: "India",
    startDate: "23 Jan 2025",
    endDate: "29 Jan 2025",
    status: "Upcoming",
  },
  {
    id: 2,
    name: "AutoExpo 2024",
    organiser: "SIAM",
    city: "New Delhi",
    country: "India",
    startDate: "17 Jan 2024",
    endDate: "22 Jan 2024",
    status: "Completed",
  },
  {
    id: 3,
    name: "PlastIndia 2024",
    organiser: "PLEXCONCIL",
    city: "Gandhinagar",
    country: "India",
    startDate: "1 Feb 2024",
    endDate: "6 Feb 2024",
    status: "Completed",
  },
  {
    id: 4,
    name: "ElecRama 2025",
    organiser: "IEEMA",
    city: "Mumbai",
    country: "India",
    startDate: "10 Mar 2025",
    endDate: "13 Mar 2025",
    status: "Active",
  },
];

const activityData = [
  { name: "Mon", visitors: 400, exhibitors: 240 },
  { name: "Tue", visitors: 300, exhibitors: 139 },
  { name: "Wed", visitors: 200, exhibitors: 980 },
  { name: "Thu", visitors: 278, exhibitors: 390 },
  { name: "Fri", visitors: 189, exhibitors: 480 },
  { name: "Sat", visitors: 239, exhibitors: 380 },
  { name: "Sun", visitors: 349, exhibitors: 430 },
];

const registrationData = [
  { name: "Jan", events: 4, users: 1200 },
  { name: "Feb", events: 3, users: 800 },
  { name: "Mar", events: 2, users: 1500 },
  { name: "Apr", events: 6, users: 2100 },
  { name: "May", events: 5, users: 1800 },
  { name: "Jun", events: 8, users: 3200 },
];

const statusColor = {
  Upcoming: "primary",
  Active: "success",
  Completed: "default",
};

const Dashboard = () => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <HeaderBar navItems={navItems} />

      <Box sx={{ p: { xs: 2, md: 4 }, flex: 1 }}>
        {/* Page Title */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Super Admin Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome back! Here's an overview of ExpoPlanner platform metrics.
          </Typography>
        </Box>

        {/* Stat Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                <CardContent sx={{ display: "flex", alignItems: "center", gap: 2, p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: card.bg,
                      color: card.color,
                      width: 60,
                      height: 60,
                    }}
                  >
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={card.color}>
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.label}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Line Chart */}
          <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Platform Activity (This Week)
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ height: 300, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={activityData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="visitors" stroke="#f57c00" strokeWidth={3} activeDot={{ r: 8 }} />
                      <Line type="monotone" dataKey="exhibitors" stroke="#7b1fa2" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Bar Chart */}
          <Grid item xs={12} lg={4}>
            <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Registrations Overview
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ height: 300, width: "100%" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={registrationData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="users" fill="#1976d2" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
           {/* Recent Events Table */}
           <Grid item xs={12} lg={8}>
            <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Recent Events
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f0f4ff" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Event Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Organiser</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentEvents.map((event) => (
                        <TableRow
                          key={event.id}
                          hover
                          sx={{ "&:last-child td": { border: 0 } }}
                        >
                          <TableCell>
                            <Typography fontWeight={600}>{event.name}</Typography>
                          </TableCell>
                          <TableCell>{event.organiser}</TableCell>
                          <TableCell>
                            {event.city}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.status}
                              color={statusColor[event.status]}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          {/* Quick Actions */}
          <Grid item xs={12} lg={4}>
            <Card elevation={2} sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Quick Actions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    component={Link}
                    to="/add-organiser"
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: "none", justifyContent: "flex-start", px: 3, py: 1.5 }}
                  >
                    Add Organiser
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<EventIcon />}
                    component={Link}
                    to="/event"
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: "none", justifyContent: "flex-start", px: 3, py: 1.5 }}
                  >
                    View Events
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PeopleAltIcon />}
                    component={Link}
                    to="/visitor"
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: "none", justifyContent: "flex-start", px: 3, py: 1.5 }}
                  >
                    View Visitors
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BusinessIcon />}
                    component={Link}
                    to="/exhibitor"
                    fullWidth
                    sx={{ borderRadius: 2, textTransform: "none", justifyContent: "flex-start", px: 3, py: 1.5 }}
                  >
                    View Exhibitors
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;
